import React from 'react';
import { pubSub } from '@tymely/app/AuthContext';
import { Subscription } from 'rxjs';
import { datadogLogs } from '@datadog/browser-logs';

type StringOrSequentiable = string | Record<string, unknown> | { seq?: number };
type UnsubscribeCallback = Subscription | undefined;

export const createSubscription = <T extends StringOrSequentiable = StringOrSequentiable>(
    topic: string,
    callback: (value: T, channel: string) => void,
    complete?: () => void,
    gapTimeoutMs: number | null = 5000,
): UnsubscribeCallback => {
    if (!topic || topic.includes('undefined')) {
        return;
    }

    let expectedSeq = 0;
    const buffer = new Map<number, T>();

    let gapTimer: ReturnType<typeof setTimeout> | null = null;

    const clearGapTimer = () => {
        if (gapTimer) {
            clearTimeout(gapTimer);
            gapTimer = null;
        }
    };

    const hasGapNow = () => Array.from(buffer.keys()).some((seq) => seq > expectedSeq);

    const startOrRestartGapTimerIfNeeded = () => {
        clearGapTimer();

        if (!hasGapNow() || gapTimeoutMs === null) {
            return;
        }

        gapTimer = setTimeout(() => {
            if (expectedSeq === 0) {
                return;
            }

            datadogLogs.logger.info(`Event (seq=${expectedSeq}) was skipped`, { topic });

            expectedSeq = expectedSeq + 1;
            flushInOrder();
            startOrRestartGapTimerIfNeeded();
        }, gapTimeoutMs);
    };

    const flushInOrder = () => {
        let next = buffer.get(expectedSeq);
        while (next !== undefined) {
            buffer.delete(expectedSeq);
            callback(next, topic);
            expectedSeq += 1;

            next = buffer.get(expectedSeq);
        }
    };

    const handleMessage = (event: T) => {
        if (typeof event === 'string' || event.seq === null || event.seq === undefined) {
            return callback(event, topic);
        }

        if (event.seq < expectedSeq) {
            datadogLogs.logger.info(`Skipped event (seq=${expectedSeq}) has arrived`, { topic });
            return;
        }

        if (event.seq === expectedSeq) {
            buffer.set(event.seq, event);
            flushInOrder();

            startOrRestartGapTimerIfNeeded();
            return;
        }

        // Event is in the future, buffer it
        buffer.set(event.seq, event);
        startOrRestartGapTimerIfNeeded();
    };

    const subscription = pubSub.subscribe({ topics: topic }).subscribe({
        next: (data) => handleMessage(data as T),
        complete: () => {
            clearGapTimer();
            complete?.();
        },
    });

    const originalUnsubscribe = subscription.unsubscribe.bind(subscription);
    subscription.unsubscribe = () => {
        clearGapTimer();
        buffer.clear();
        originalUnsubscribe();
    };

    return subscription;
};

export const useSubscription = <T extends StringOrSequentiable = StringOrSequentiable>(
    topic: string,
    callback: (value: T) => void,
    complete?: () => void,
    deps: React.DependencyList = [],
    enabled?: boolean,
) => {
    const subscriptionRef = React.useRef<UnsubscribeCallback>(undefined);

    React.useEffect(() => {
        if (enabled === false) {
            return;
        }

        subscriptionRef.current = createSubscription(topic, callback, complete);

        return () => {
            subscriptionRef.current?.unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topic, ...deps]);

    React.useEffect(() => () => subscriptionRef.current?.unsubscribe(), []);
};

export const useMultiSubscriptions = <T extends StringOrSequentiable = StringOrSequentiable>(
    topics: string[],
    callback: (value: T, channel: string) => void,
    deps: React.DependencyList = [],
) => {
    const channelsRef = React.useRef(new Map<string, UnsubscribeCallback>());

    React.useEffect(() => {
        topics.forEach((topic) => {
            const subscription = channelsRef.current.get(topic);
            if (!subscription || subscription.closed) {
                channelsRef.current.set(topic, createSubscription(topic, callback));
            }
        });

        channelsRef.current.forEach((subscription, channel) => {
            if (!topics.includes(channel)) {
                subscription?.unsubscribe();
                channelsRef.current.delete(channel);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topics, ...deps]);

    React.useEffect(
        () => () => {
            channelsRef.current.forEach((subscription) => subscription?.unsubscribe());
            channelsRef.current.clear();
        },
        [],
    );
};

export interface IEvent<T> {
    object_type: string;
    object_id: number | null;
    action: string | null;
    data: T;
    seq?: number;
}

export type UseEventSubscriptionArgs<E> = {
    topic: string;
    enabled?: boolean;
    filter?: (event: E) => boolean;
};

export const useEventSubscription = <T, E extends StringOrSequentiable = IEvent<T>>(
    { topic, enabled, filter }: UseEventSubscriptionArgs<E>,
    callback: (value: E) => void,
    complete?: () => void,
    deps: React.DependencyList = [],
) =>
    useSubscription<E>(
        topic,
        (value) => {
            if (filter === undefined || filter(value)) {
                callback(value);
            }
        },
        complete,
        deps,
        enabled,
    );

type PersistentEvent = Record<string, unknown> & { event: string; interval?: number };

export const usePersistentEvent = <T extends PersistentEvent = PersistentEvent>(
    channel: string | null,
    { interval = 5000, ...data }: T,
    deps: React.DependencyList = [],
    enabled = true,
) => {
    const intervalRef = React.useRef<NodeJS.Timer | undefined>(undefined);

    const stop = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
    };

    React.useEffect(() => {
        if (!enabled) return;
        if (!channel || channel.includes('undefined')) {
            return;
        }
        intervalRef.current = setInterval(() => pubSub.publish({ topics: channel, message: data }), interval);
        return stop;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel, interval, ...deps]);

    React.useEffect(() => stop, []);
};
